import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { withoutBidiControls as plain } from "../snippets/bidi";
import ApprovalReading from "./ApprovalReading";
import CurrentTargetPanel from "./CurrentTargetPanel";
import DecideTargetDialog from "./DecideTargetDialog";
import SyncPlanPanel from "./SyncPlanPanel";
import TargetChangePanel from "./TargetChangePanel";
import TargetHistory from "./TargetHistory";
import UnauthorizedBanner from "./UnauthorizedBanner";
import useLatestSnapshot from "./useLatestSnapshot";
import { taskBoardPath } from "./lib/format";
import { asSentence } from "./lib/outcome";

/**
 * What a decision did, said once it landed (the decide response) — or that
 * a 409's reload decided nothing, or that no answer came back, so it may or
 * may not have landed.
 */
function DecisionNote({ decided, reloaded, uncertain }) {
  if (uncertain) {
    const { number, decision, message } = uncertain;
    const verb = decision === "approved" ? "approved" : "declined";
    const check =
      decision === "approved"
        ? `if v${number} is the current target, it was approved`
        : `if v${number} is in the version history as declined, it was`;
    return (
      <>
        Couldn&apos;t tell whether v{number} was {verb}: {asSentence(message)} It may or may not have gone through — the
        target was read again: {check}; if it&apos;s still the open change, decide again.
      </>
    );
  }
  if (reloaded) {
    return <>Reloaded — nothing was decided. Read the change as it stands now, then decide again.</>;
  }
  if (!decided) return null;
  const { number, status, sync_task: task } = decided;
  if (status === "declined") {
    return <>Declined — v{number} is kept in the history; the current target is unchanged.</>;
  }
  return (
    <>
      Approved — v{number} is the current target.{" "}
      {task ? (
        <>
          Your executor&apos;s board task:{" "}
          <Link
            to={taskBoardPath(task.id)}
            className="inline-flex min-h-11 items-center underline underline-offset-2 md:min-h-0"
          >
            {plain(task.title)} (task #{task.id})
          </Link>
          .
        </>
      ) : (
        "Its sync task is on the board, assigned to your executor."
      )}
    </>
  );
}

DecisionNote.propTypes = {
  /** The decided version as the API returned it; null before a decision. */
  decided: PropTypes.shape({
    number: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    sync_task: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }),
  }),
  /** A 409's reload landed instead of a decision. */
  reloaded: PropTypes.bool,
  /** No answer or a 5xx: `{number, decision, message}` — it may or may not have landed. */
  uncertain: PropTypes.shape({
    number: PropTypes.number.isRequired,
    decision: PropTypes.string.isRequired,
    message: PropTypes.string,
  }),
};

/**
 * The Target page (spec §5.4, walkthroughs 7–9), at /investing/target:
 * Robinhood's approval reading from the latest snapshot; the change open
 * against the current target, as a diff, with roughly the money it moves;
 * Approve / Decline (a person's — failing closed while `me` is unknown,
 * never for an agent — behind a confirm that states the new target and
 * sends the revision shown); the current target; the latest sync plan with
 * its orders and fills; the version history. Above it all, the red banner:
 * any unauthorized fill not yet acknowledged (walkthrough 10).
 */
function InvestingTarget() {
  const { me } = useOutletContext() || {};
  const isPerson = me?.kind === "human";
  const isAgent = me?.kind === "agent";

  const [reloadTick, setReloadTick] = useState(0);
  const [target, setTarget] = useState(null);
  const [loadingTarget, setLoadingTarget] = useState(true);
  const [failure, setFailure] = useState(null);
  const [executor, setExecutor] = useState(null); // {username, valid} from Settings
  // The confirm: the decision and the version as it was shown when it
  // opened (kept through the closing render; `dialogOpen` opens it).
  const [deciding, setDeciding] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [decided, setDecided] = useState(null);
  const [reloaded, setReloaded] = useState(false);
  const [uncertain, setUncertain] = useState(null);
  // A decision lands and the reload takes the Approve button away (or a
  // 409's reload disables it while it runs): focus goes to what happened
  // instead of falling to <body>.
  const noteRef = useRef(null);
  const focusNoteRef = useRef(false);
  const latest = useLatestSnapshot(reloadTick);

  const requestRef = useRef(0);
  useEffect(() => {
    const request = ++requestRef.current;
    setLoadingTarget(true);
    investingApi.target.get().then(
      ({ data }) => {
        if (request !== requestRef.current) return;
        setTarget(data);
        setFailure(null);
        setLoadingTarget(false);
      },
      (err) => {
        if (request !== requestRef.current) return;
        setFailure(apiErrorMessage(err, "Couldn't load the target."));
        setLoadingTarget(false);
      },
    );
  }, [reloadTick]);

  // Who the sync task would go to — for the confirm (a person's only).
  useEffect(() => {
    if (!isPerson) return undefined;
    let live = true;
    investingApi.settings.get().then(
      ({ data }) => {
        if (live) setExecutor({ username: data?.executor_username ?? null, valid: Boolean(data?.executor_valid) });
      },
      () => {
        if (live) setExecutor(null);
      },
    );
    return () => {
      live = false;
    };
  }, [isPerson, reloadTick]);

  const reload = useCallback(() => setReloadTick((tick) => tick + 1), []);

  const openDecide = (decision) => {
    if (!target?.open) return;
    setDecided(null);
    setReloaded(false);
    setUncertain(null);
    setDeciding({ decision, version: target.open, current: target.current });
    setDialogOpen(true);
  };

  const onDecided = useCallback(
    (data) => {
      focusNoteRef.current = true;
      setDialogOpen(false);
      setDecided(data);
      reload();
    },
    [reload],
  );

  const onConflictReload = useCallback(() => {
    focusNoteRef.current = true;
    setDialogOpen(false);
    setReloaded(true);
    reload();
  }, [reload]);

  const onUnknown = useCallback(
    (what) => {
      focusNoteRef.current = true;
      setDialogOpen(false);
      setUncertain(what);
      reload();
    },
    [reload],
  );

  const onCloseAutoFocus = useCallback((event) => {
    if (!focusNoteRef.current) return;
    focusNoteRef.current = false;
    event.preventDefault();
    noteRef.current?.focus();
  }, []);

  // A decision landed but the reload that would take the change away
  // failed: the change still shown is already decided — not pressable.
  const decidedShown = Boolean(decided && target?.open && decided.number === target.open.number);

  let main;
  if (!target) {
    main = failure ? (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>{failure}</span>{" "}
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={reload}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    ) : (
      <div className="py-12 text-center" role="status">
        <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Loading the target…</span>
      </div>
    );
  } else {
    main = (
      <div className="flex flex-col gap-4">
        {failure && (
          <Alert variant="destructive" role="alert">
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{failure} The page shows what it last loaded.</span>{" "}
              <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={reload}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {target.open ? (
          <TargetChangePanel
            version={target.open}
            current={target.current}
            openTotal={target.open_total ?? 1}
            latest={latest}
            canDecide={isPerson}
            busy={loadingTarget || decidedShown}
            isAgent={isAgent}
            onDecide={openDecide}
          />
        ) : (
          <section aria-label="Open change" className="rounded-lg border bg-card p-4">
            <h2 className="mt-0 mb-1 text-lg font-semibold">Open change</h2>
            <p className="m-0 text-sm">
              No change is open. When research finds an opportunity, an exit condition hits or you ask for a
              portfolio review, an agent drafts a change and it shows here as a diff against the current target.
            </p>
          </section>
        )}
        <CurrentTargetPanel version={target.current} />
        <SyncPlanPanel reloadTick={reloadTick} />
        <TargetHistory reloadTick={reloadTick} />
      </div>
    );
  }

  return (
    <div className="page-container mt-6 pt-4">
      <div className="max-w-4xl">
        <header className="mb-4">
          <h1 className="mb-1 font-display text-3xl font-semibold tracking-tight">Target</h1>
          <p className="m-0 text-sm text-muted-foreground">
            What the account should hold: each holding&apos;s weight and why, and cash. Agents draft changes; you
            approve or decline them here; your executor then syncs the account to the approved target, and Robinhood
            asks you to approve each order on your phone.
          </p>
          <ApprovalReading latest={latest} />
        </header>
        <UnauthorizedBanner />
        <div
          ref={noteRef}
          role="status"
          aria-label="Decision"
          tabIndex={-1}
          className={
            uncertain
              ? "mb-4 rounded-md border border-warning/60 bg-warning/10 p-3 text-sm outline-none"
              : decided
                ? "mb-4 rounded-md border border-success/60 bg-success/10 p-3 text-sm outline-none"
                : reloaded
                  ? "mb-4 rounded-md border border-border bg-muted/40 p-3 text-sm outline-none"
                  : "m-0"
          }
        >
          <DecisionNote decided={decided} reloaded={reloaded} uncertain={uncertain} />
        </div>
        {main}
      </div>
      {isPerson && deciding && (
        <DecideTargetDialog
          open={dialogOpen}
          decision={deciding.decision}
          version={deciding.version}
          current={deciding.current}
          latest={latest}
          executor={executor}
          onOpenChange={setDialogOpen}
          onDecided={onDecided}
          onReload={onConflictReload}
          onUnknown={onUnknown}
          onCloseAutoFocus={onCloseAutoFocus}
        />
      )}
    </div>
  );
}

export default InvestingTarget;
